import { DOCUMENT, isPlatformBrowser } from '@angular/common';
import { computed, inject, Injectable, PLATFORM_ID } from '@angular/core';
import { Order } from '@idealsupply/ngclient-webservice-orders';

import { PunchoutCart } from '@idealsupply/ngclient-webservice-cxml';
import { AuthenticationStore, createUserProxy } from 'authentication-data';
// import { CartService } from 'lib-cart';
import { APP_CONFIG } from 'config-data';
import { OrdersService } from 'lib-orders';
import { CXML_API_PATH } from './constants';
import { PunchoutUser } from './PunchoutUser';
import { PunchoutCartComponentStore } from './store';

@Injectable({
  providedIn: 'root',
})
export class PunchoutOrdersService extends OrdersService {
  private readonly _isBrowser = isPlatformBrowser(inject(PLATFORM_ID));
  private readonly _cartsStore = inject(PunchoutCartComponentStore);
  private readonly _authStore = inject(AuthenticationStore);
  private readonly _document = inject(DOCUMENT);
  private readonly _config = inject(APP_CONFIG);

  private readonly _user = computed(() =>
    this._authStore.userInfo() ? createUserProxy<PunchoutUser>(this._authStore.userInfo()!, PunchoutUser) : null,
  );

  public override async submitOrder(_ccInfo?: { name: string; cardType: string }): Promise<Order> {
    if (!this._isBrowser) {
      throw new Error('Punchout checkout must run in the browser.');
    }

    const user = this._user();
    if (!user?.sessionId) {
      throw new Error('No punchout session ID available.');
    }

    const ready = this._cartsStore.punchoutCartReady();
    if (!ready) {
      throw new Error('Punchout cart is not ready.');
    }

    const itemCount = this._cartsStore.cartItemCount();
    if (!itemCount) {
      throw new Error('Punchout cart is empty.');
    }

    this.deleteOrderData();
    await this._cartsStore.clearCart();

    this.submitCartForm(this._cartsStore.punchoutCart());
    return {} as Order;
  }

  /**
   * Creates and submits a form that POSTs cart data to the backend.
   * Backend will respond with HTML containing an auto-submit form.
   */
  private submitCartForm(punchoutCart: PunchoutCart): void {
    const body = this._document.body;
    if (!body) {
      throw new Error('Unable to submit punchout order message.');
    }

    const form = this._document.createElement('form');
    form.method = 'POST';
    form.action = `${this._config.auth?.apiRoot}${CXML_API_PATH}/order`;
    form.acceptCharset = 'UTF-8';
    form.style.display = 'none';

    // Add cart data as JSON in hidden input
    const cartInput = this._document.createElement('input');
    cartInput.type = 'hidden';
    cartInput.name = 'cart';
    cartInput.value = JSON.stringify(punchoutCart);

    // Add Bearer token as form parameter
    const tokenInput = this._document.createElement('input');
    tokenInput.type = 'hidden';
    tokenInput.name = 'token';
    tokenInput.value = this._authStore.accessToken() || '';

    form.appendChild(cartInput);
    form.appendChild(tokenInput);
    body.appendChild(form);

    // Submit on next tick to ensure DOM is ready
    setTimeout(() => form.submit(), 0);
  }
}
